import { Controller } from "@hotwired/stimulus"
let debounce = require("lodash/debounce");

export default class extends Controller {
    static targets = ['variantId', 'quantity', 'updateForm', 'input', 'toggler', 'editInvoice',
                      'itemQuantity', 'deleteItem', 'invoiceHeader', 'searchForm', 'updateVersion'];

    initialize() {
        this.submit = debounce(this.submit, 700).bind(this);
        this.loading = false;
    }

    toggle(event) {
        event.preventDefault();
        event.stopPropagation();
        this.togglerTarget.classList.add('d-none');
        this.editInvoiceTarget.classList.remove('d-none');
    }

    editInvoice(event) {
        event.preventDefault();
        event.stopPropagation();

        this.toggleInvoiceInputs();
    }

    toggleInvoiceInputs() {
        this.itemQuantityTargets.forEach((item) => item.classList.add('d-none'));
        this.inputTargets.forEach((item) => item.classList.remove('d-none'));
        this.deleteItemTargets.forEach((item) => item.classList.remove('d-none'));
        this.invoiceHeaderTarget.classList.add('d-none');
        this.searchFormTarget.classList.remove('d-none');
    }

    setBale({ detail: { bale } }) {
        this.quantityTarget.min = bale;
        this.quantityTarget.step = bale;
        this.quantityTarget.value = bale;
    }

    submit() {
        this.disposePopovers();
        this.updateFormTarget.requestSubmit();
    }

    // The form's data-action binding in _invoice.html.haml maps Turbo's submit lifecycle events
    // to these methods. Turbo owns this round-trip instead of the custom:load event the
    // re-rendered partial used to dispatch back from an inline script.
    // submit-start fires after Turbo has snapshotted FormData, so disabling inputs here cannot
    // drop them from the payload.
    submitStart() {
        this.loading = true;
        this.disableInputs();
    }

    // The same data-action binding maps turbo:submit-end to this method. Turbo dispatches it from
    // a finally block, so a failed update unlocks the form for a retry.
    submitEnd() {
        this.loading = false;
        this.disableInputs(false);
    }

    // on=true locks qty inputs; on=false unlocks. Used by the submit lifecycle and processEventForRequest.
    disableInputs(on = true) {
        this.inputTargets.forEach((input) => {
            input.disabled = on;
            input.parentNode.classList.toggle('loading', on);
        });
    }

    disposePopovers() {
        document.dispatchEvent(new Event('custom:dispose-popovers'));
    }

    add(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.variantIdTarget.value === '') { return }
        if (!this.quantityTarget.reportValidity()) { return }

        const formData = new FormData();
        formData.append('variant_id', this.variantIdTarget.value);
        formData.append('quantity', this.quantityTarget.value);
        // Sent back so the re-rendered form shows the same search text
        formData.append('search_text', this.searchFormTarget.querySelector('[data-modify-search-target="searchInput"]').value);
        formData.append('authenticity_token', AUTH_TOKEN);
        formData.append('update_version', this.updateVersionTarget.value);

        this.processEventForRequest(event, formData, 'POST');
    }

    remove(event) {
        event.preventDefault();
        event.stopPropagation();

        const formData = new FormData();
        formData.append('variants[][variant_id]', event.params.id);
        formData.append('variants[][quantity]', event.params.quantity);
        formData.append('authenticity_token', AUTH_TOKEN);
        formData.append('update_version', this.updateVersionTarget.value);

        this.processEventForRequest(event, formData, 'PATCH');
    }

    async processEventForRequest(event, formData, method) {
        if (this.loading) { return }

        // currentTarget is nulled once the handler returns, so keep the control we mutate.
        const control = event.currentTarget;
        const controlHtml = control.innerHTML;

        this.loading = true;
        control.classList.add('disabled');
        control.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
        this.disableInputs();
        this.disposePopovers();

        try {
            const html = await fetch(control.href, {
                method,
                body: formData,
                headers: {
                    'Accept': 'text/vnd.turbo-stream.html',
                }
            }).then(r => r.text())
            Turbo.renderStreamMessage(html)
        } finally {
            // A failed request re-renders nothing, so the form would stay locked forever. Undo the
            // lock here instead of catching: the error still propagates, but the user can retry.
            // On success these nodes are replaced by the stream, so the restore is a no-op.
            this.loading = false;
            control.classList.remove('disabled');
            control.innerHTML = controlHtml;
            this.disableInputs(false);
        }
    }
}
